import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { JureSynthese } from 'src/app/data/commission/models/jure-synthese.model';
import { JuresService } from 'src/app/data/commission/services/jures/jures.service';
import { forkJoin } from 'rxjs';
import { ExportToCsv } from 'export-to-csv';
import { DetailSyntheseComponent } from './modals/detail-synthese/detail-synthese.component';
import { CollegeService } from '../../../../../data/commission/services/college/college.service';
import { CahiersService } from '../../../../../data/commission/services/cahiers/cahiers.service';
import { College } from '../../../../../data/commission/models/college.model';
import { Cahier } from '../../../../../data/habilitation/models/cahier.model';
import { EvaluationJuresService } from '../../../../../data/commission/services/evaluation-jures/evaluation-jures.service';
import { DetailEvaluationComponent } from './modals/detail-evaluation/detail-evaluation.component';
import { map, switchMap } from 'rxjs/operators';
import { EvaluationJure } from '../../../../../data/commission/models/evaluation-jure.model';
import { CriteresEvaluationJure } from '../../../../../data/commission/models/criteres-evaluation-jure.model';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion';
import { FloatLabel } from 'primeng/floatlabel';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Button } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { Badge } from 'primeng/badge';
import { Column } from '../../../../../shared/models/column.model';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { JureCdc } from '../../../../../data/commission/models/jure-cdc.model';

@Component({
    selector: 'app-synthese-jure',
    templateUrl: './synthese-jure.component.html',
    styleUrls: ['./synthese-jure.component.scss'],
    imports: [
        Accordion,
        AccordionPanel,
        AccordionHeader,
        AccordionContent,
        ReactiveFormsModule,
        FloatLabel,
        DatePicker,
        Select,
        MultiSelect,
        Button,
        TranslateModule,
        Badge,
        TableModule,
        TooltipModule,
        DetailSyntheseComponent,
        DetailEvaluationComponent
    ],
    standalone: true
})
export class SyntheseJureComponent implements OnInit {
    /**
     * Signals
     */
    showDetailModal = signal(false);
    showEvaluationModal = signal(false);

    /**
     * Id de l'organisme
     */
    protected idOrganisme: number;

    /**
     * Synthèse des jurés
     */
    public syntheses: JureSynthese[] = [];

    /**
     * Formulaire de recherche
     */
    public formSearchCommissions: UntypedFormGroup;

    /**
     * Liste des collèges
     */
    public collegeList: College[] = [];

    /**
     * Liste des cahiers des charges
     */
    public cahiersList: Cahier[] = [];

    /**
     * Détail des jurés pour la modale
     */
    detailJures: JureCdc[] = [];

    /**
     * Juré sélectionné pour la modale detail
     */
    selectedJure: JureSynthese | null = null;

    selectedDateDebut: moment.Moment;

    selectedDateFin: moment.Moment;

    /**
     * Liste des évaluations sur la période
     */
    evaluationsPeriode: EvaluationJure[] = [];

    /**
     * Liste des critères d'évaluation sur la période
     */
    criteresPeriode: CriteresEvaluationJure[] = [];

    /**
     * Date de début avec une valeur par défaut
     */
    dateDebut: moment.Moment = moment().startOf('year');

    /**
     * Date de fin avec une valeur par défaut
     */
    dateFin: moment.Moment = moment().endOf('year');

    /**
     * Liste des dates de la période sélectionnée
     */
    rangeDates: Date[] = [];

    /**
     * Liste des colonnes à afficher dans le tableau
     */
    columns: Column[] = [
        { field: 'nom', label: 'page.commissions.synthese-jure.table.nom', filter: 'text', width: '150px', frozen: true },
        { field: 'mail', label: 'page.commissions.synthese-jure.table.mail', filter: 'text', width: '150px', frozen: true },
        { field: 'nbConvocations', label: 'page.commissions.synthese-jure.table.nbConvocations', filter: 'numeric', width: '100px', frozen: false },
        { field: 'nbPresent', label: 'page.commissions.synthese-jure.table.nbPresent', filter: 'numeric', width: '100px', frozen: false },
        { field: 'nbExcuse', label: 'page.commissions.synthese-jure.table.nbExcuse', filter: 'numeric', width: '100px', frozen: false },
        { field: 'nbAbsent', label: 'page.commissions.synthese-jure.table.nbAbsent', filter: 'numeric', width: '100px', frozen: false },
        { field: 'nbConvoque', label: 'page.commissions.synthese-jure.table.nbConvoque', filter: 'numeric', width: '100px', frozen: false },
        { field: 'nbAConvoque', label: 'page.commissions.synthese-jure.table.nbAConvoque', filter: 'numeric', width: '120px', frozen: false },
        { field: 'evaluation', label: 'page.commissions.synthese-jure.table.evaluation', filter: 'text', width: '100px', frozen: false }
    ];

    /**
     * Référence au composant de tableau PrimeNG
     */
    @ViewChild('dt') dt: Table;

    /**
     * Valeur active de l'accordéon (null/undefined = fermé)
     */
    accordionValue: string | null | undefined = '0';

    /**
     * Scroll height du tableau selon l'état de l'accordéon
     */
    get tableScrollHeight(): string {
        return this.accordionValue === undefined ? 'calc(100vh - 320px)' : 'calc(100vh - 440px)';
    }

    /**
     * Mise à jour de l'état de l'accordéon
     */
    onAccordionChange(value: string | undefined): void {
        this.accordionValue = value;
    }

    /**
     * Getter cahier des charges
     */
    get cahiers() {
        return this.formSearchCommissions.get('cahierCommission');
    }

    /**
     * Getter collège
     */
    get college() {
        return this.formSearchCommissions.get('collegeCommission');
    }

    /**
     * Getter dates de la période sélectionnée
     */
    get rangeDatesCommission() {
        return this.formSearchCommissions.get('rangeDates');
    }

    /**
     * Getter nombre de filtres actifs
     */
    get activeFilters(): number {
        let count = 0;
        if (this.rangeDatesCommission.value?.some(d => d !== null)) {
            count++;
        }
        if (this.college.value) {
            count++;
        }
        if (this.cahiers.value?.length) {
            count++;
        }
        return count;
    }

    /**
     * Dependencies injection
     */
    private readonly fb = inject(UntypedFormBuilder);
    private readonly loaderService = inject(LoaderService);
    private readonly jureService = inject(JuresService);
    private readonly collegeService = inject(CollegeService);
    private readonly cahiersService = inject(CahiersService);
    private readonly evaluationJuresService = inject(EvaluationJuresService);

    /**
     * On init component
     */
    ngOnInit() {
        // Récupérer l'ID organisme
        this.idOrganisme = SessionContext.get('idOrganisme');

        // Instancier le formulaire de recherche
        this.formSearchCommissions = this.fb.group({
            rangeDates: [null],
            collegeCommission: undefined,
            cahierCommission: undefined
        });

        // Charger les données pour les listes déroulantes
        this.loadData();
        this.onSubmit();
    }

    /**
     * Load data
     */
    private loadData(): void {
        const dates: Date[] = this.rangeDatesCommission.value ?? [];
        const dateDebut = dates[0] ? moment(dates[0]) : null;
        const dateFin = dates[1] ? moment(dates[1]) : null;

        this.jureService
            .getSyntheseJures(this.idOrganisme, dateDebut, dateFin, this.college.value, this.cahiers.value)
            .pipe(
                switchMap(synthese => {
                    this.syntheses = synthese;
                    const idsJures = synthese.map(s => s.id);

                    return forkJoin([
                        this.collegeService.getColleges(),
                        this.cahiersService.getCahiers(),
                        this.evaluationJuresService.getEvaluationsJures(idsJures, dateDebut, dateFin).pipe(
                            switchMap(evaluations => {
                                this.evaluationsPeriode = evaluations;
                                const idsCriteres = evaluations.map(e => e.idCritereEvaluation);
                                return this.evaluationJuresService.getCriteresEvaluationJuresByIds(this.idOrganisme, idsCriteres);
                            })
                        )
                    ]).pipe(
                        map(([colleges, cahiers, criteres]) => ({
                            colleges,
                            cahiers,
                            criteres
                        }))
                    );
                }),
                this.loaderService.operator()
            )
            .subscribe(({ colleges, cahiers, criteres }) => {
                this.collegeList = colleges.sort((a, b) => a.libelle.localeCompare(b.libelle));
                this.cahiersList = cahiers.sort((a, b) => a.libelle.localeCompare(b.libelle));
                this.criteresPeriode = criteres;
            });
    }

    /**
     * On submit formulaire de recherche
     */
    public onSubmit(): void {
        const dates: Date[] = this.rangeDatesCommission.value ?? [];
        this.jureService
            .getSyntheseJures(this.idOrganisme, dates[0] ? moment(dates[0]) : null, dates[1] ? moment(dates[1]) : null, this.college.value, this.cahiers.value)
            .pipe(this.loaderService.operator())
            .subscribe(synthese => {
                this.syntheses = synthese;
            });
    }

    /**
     * View detail
     * @param jury - Détail du juré
     */
    viewDetail(jury: JureSynthese) {
        const dates: Date[] = this.rangeDatesCommission.value ?? [];
        const dateDebut = dates[0] ? moment(dates[0]) : null;
        const dateFin = dates[1] ? moment(dates[1]) : null;
        this.jureService
            .getDetailJures(this.idOrganisme, jury.id, dateDebut, dateFin)
            .pipe(this.loaderService.operator())
            .subscribe(detail => {
                this.detailJures = detail;
                this.selectedJure = jury;
                this.showDetailModal.set(true);
            });
    }

    /**
     * Affiche le détail d'évaluation du juré sélectionné
     * @param jure - Le juré sélectionné
     */
    viewEvaluation(jure: JureSynthese) {
        const dates: Date[] = this.rangeDatesCommission.value ?? [];
        this.selectedDateDebut = dates[0] ? moment(dates[0]) : null;
        this.selectedDateFin = dates[1] ? moment(dates[1]) : null;
        this.selectedJure = jure;
        this.showEvaluationModal.set(true);
    }

    /**
     * Exporter synthèse des jurés
     */
    public onExport(): void {
        const data = [];
        const synthesesAExporter = this.dt.filteredValue ?? this.syntheses;
        synthesesAExporter.forEach(synthese => {
            data.push({
                nom: synthese.nom,
                mail: synthese.mail,
                adresseJure: synthese.adresseJure ? synthese.adresseJure : '',
                complementAdresseJure: synthese.complementAdresseJure ? synthese.complementAdresseJure : '',
                codePostalJure: synthese.codePostalJure ? synthese.codePostalJure : '',
                villeJure: synthese.villeJure ? synthese.villeJure : '',
                dateCommission: moment(synthese.dateCommission).format('YYYY-MM-DD'),
                numeroCommission: synthese.numeroCommission,
                nomSalle: synthese.nomSalle,
                adresseSalle: synthese.adresseSalle,
                villeSalle: synthese.villeSalle,
                libelleCollege: synthese.libelleCollege,
                cahierDesCharges: synthese.cahierDesCharges.join(', '),
                nbConvocation: synthese.nbConvocation,
                nbPresent: synthese.nbPresent,
                nbExcuse: synthese.nbExcuse,
                nbAbsent: synthese.nbAbsent,
                nbDesinscrit: synthese.nbDesinscrit,
                nbConvoque: synthese.nbConvoque,
                nbAConvoque: synthese.nbAConvoque
            });
        });

        const options = {
            filename: 'EXPORT_SYNTHESE'.concat('_').concat(moment().format('YYYYMMDD')),
            fieldSeparator: ';',
            quoteStrings: '',
            decimalSeparator: ',',
            showLabels: true,
            showTitle: false,
            title: 'EXPORT_SYNTHESE'.concat('_').concat(moment().format('YYYYMMDD')),
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                'Juré',
                'Email',
                'Adresse',
                "Complément d'adresse",
                'Code postal',
                'Ville',
                'Date commission',
                'N° commission',
                'Lieu de la commission',
                'Adresse du lieu',
                'Ville du lieu',
                'Collège',
                'Cahier(s) des charges',
                'Convocation',
                'Présent',
                'Excusé',
                'Absent',
                'Désinscrit',
                'Convoqué',
                'A convoquer'
            ]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(data);
    }

    /**
     * Calcule le nombre de points
     * @param idJure - Liste des évaluations
     */
    calculPoints(idJure: number): number {
        const evaluations = this.evaluationsPeriode.filter(e => e.idJure === idJure);
        let result = 0;
        if (evaluations && this.criteresPeriode.length > 0) {
            evaluations.forEach(evaluation => {
                const critere = this.criteresPeriode.find(c => c.id === evaluation.idCritereEvaluation);
                result += evaluation.valeurOui ? critere.pointsOui : critere.pointsNon;
            });
        }
        return result;
    }

    /**
     * Calcule le nombre de points maximum possible
     * @param idJure - Liste des évaluations
     */
    calculMaxPoints(idJure: number): number {
        const evaluations = this.evaluationsPeriode.filter(e => e.idJure === idJure);
        let result = 0;
        if (evaluations && this.criteresPeriode.length > 0) {
            evaluations.forEach(evaluation => {
                const critere = this.criteresPeriode.find(c => c.id === evaluation.idCritereEvaluation);
                result += critere.pointsOui;
            });
        }
        return result;
    }
}
